import React, { useState } from "react";

import { Box, Popover, TextField, Typography } from "@mui/material";
import { colors } from "@rubi/ui";
import { useTranslation } from "react-i18next";

export interface Language {
  code: string;
  name: string;
  nativeName: string;
  direction: "ltr" | "rtl";
}

export const SUPPORTED_LANGUAGES: Language[] = [
  { code: "ar", direction: "rtl", name: "Arabic", nativeName: "العربية" },
  { code: "bn", direction: "ltr", name: "Bangla", nativeName: "বাংলা" },
  { code: "bg", direction: "ltr", name: "Bulgarian", nativeName: "Български" },
  { code: "ca", direction: "ltr", name: "Catalan", nativeName: "Català" },
  {
    code: "zh",
    direction: "ltr",
    name: "Chinese Simplified",
    nativeName: "简体中文",
  },
  {
    code: "zh-Hant",
    direction: "ltr",
    name: "Chinese Traditional",
    nativeName: "繁體中文",
  },
  { code: "hr", direction: "ltr", name: "Croatian", nativeName: "Hrvatski" },
  { code: "cs", direction: "ltr", name: "Czech", nativeName: "Čeština" },
  { code: "da", direction: "ltr", name: "Danish", nativeName: "Dansk" },
  { code: "nl", direction: "ltr", name: "Dutch", nativeName: "Nederlands" },
  { code: "en", direction: "ltr", name: "English", nativeName: "English" },
  { code: "et", direction: "ltr", name: "Estonian", nativeName: "Eesti" },
  { code: "fil", direction: "ltr", name: "Filipino", nativeName: "Filipino" },
  { code: "fi", direction: "ltr", name: "Finnish", nativeName: "Suomi" },
  { code: "fr", direction: "ltr", name: "French", nativeName: "Français" },
  {
    code: "fr-ca",
    direction: "ltr",
    name: "French (Canada)",
    nativeName: "Français (Canada)",
  },
  { code: "de", direction: "ltr", name: "German", nativeName: "Deutsch" },
  { code: "el", direction: "ltr", name: "Greek", nativeName: "Ελληνικά" },
  { code: "gu", direction: "ltr", name: "Gujarati", nativeName: "ગુજરાતી" },
  { code: "he", direction: "rtl", name: "Hebrew", nativeName: "עברית" },
  { code: "hi", direction: "ltr", name: "Hindi", nativeName: "हिन्दी" },
  { code: "hu", direction: "ltr", name: "Hungarian", nativeName: "Magyar" },
  { code: "is", direction: "ltr", name: "Icelandic", nativeName: "Íslenska" },
  {
    code: "id",
    direction: "ltr",
    name: "Indonesian",
    nativeName: "Bahasa Indonesia",
  },
  { code: "it", direction: "ltr", name: "Italian", nativeName: "Italiano" },
  { code: "ja", direction: "ltr", name: "Japanese", nativeName: "日本語" },
  { code: "kn", direction: "ltr", name: "Kannada", nativeName: "ಕನ್ನಡ" },
  { code: "ko", direction: "ltr", name: "Korean", nativeName: "한국어" },
  { code: "lv", direction: "ltr", name: "Latvian", nativeName: "Latviešu" },
  { code: "lt", direction: "ltr", name: "Lithuanian", nativeName: "Lietuvių" },
  { code: "ml", direction: "ltr", name: "Malayalam", nativeName: "മലയാളം" },
  { code: "mr", direction: "ltr", name: "Marathi", nativeName: "मराठी" },
  {
    code: "nb",
    direction: "ltr",
    name: "Norwegian Bokmål",
    nativeName: "Norsk Bokmål",
  },
  { code: "fa", direction: "rtl", name: "Persian", nativeName: "فارسی" },
  { code: "pl", direction: "ltr", name: "Polish", nativeName: "Polski" },
  {
    code: "pt",
    direction: "ltr",
    name: "Portuguese (Brazil)",
    nativeName: "Português (Brasil)",
  },
  {
    code: "pt-pt",
    direction: "ltr",
    name: "Portuguese (Portugal)",
    nativeName: "Português (Portugal)",
  },
  { code: "pa", direction: "ltr", name: "Punjabi", nativeName: "ਪੰਜਾਬੀ" },
  { code: "ro", direction: "ltr", name: "Romanian", nativeName: "Română" },
  { code: "ru", direction: "ltr", name: "Russian", nativeName: "Русский" },
  {
    code: "sr-Cyrl",
    direction: "ltr",
    name: "Serbian (Cyrillic)",
    nativeName: "Српски",
  },
  { code: "sk", direction: "ltr", name: "Slovak", nativeName: "Slovenčina" },
  {
    code: "sl",
    direction: "ltr",
    name: "Slovenian",
    nativeName: "Slovenščina",
  },
  { code: "es", direction: "ltr", name: "Spanish", nativeName: "Español" },
  {
    code: "sw",
    direction: "ltr",
    name: "Swahili (Latin)",
    nativeName: "Kiswahili",
  },
  { code: "sv", direction: "ltr", name: "Swedish", nativeName: "Svenska" },
  { code: "ta", direction: "ltr", name: "Tamil", nativeName: "தமிழ்" },
  { code: "te", direction: "ltr", name: "Telugu", nativeName: "తెలుగు" },
  { code: "th", direction: "ltr", name: "Thai", nativeName: "ไทย" },
  { code: "tr", direction: "ltr", name: "Turkish", nativeName: "Türkçe" },
  { code: "uk", direction: "ltr", name: "Ukrainian", nativeName: "Українська" },
  { code: "ur", direction: "rtl", name: "Urdu", nativeName: "اردو" },
  {
    code: "vi",
    direction: "ltr",
    name: "Vietnamese",
    nativeName: "Tiếng Việt",
  },
  { code: "zu", direction: "ltr", name: "Zulu", nativeName: "isiZulu" },
];

export const getLanguageInfo = (code: string): Language | undefined =>
  SUPPORTED_LANGUAGES.find((lang) => lang.code === code);

interface BaseLanguageSelectorProps {
  languages?: Language[];
  columns?: number;
  triggerMinWidth?: number | string;
  menuColumnWidth?: number;
  selectedDisplayMode?: "name" | "code";
}

type LanguageSelectorProps =
  | (BaseLanguageSelectorProps & {
      multiple?: false;
      value: string;
      onChange: (code: string) => void;
    })
  | (BaseLanguageSelectorProps & {
      multiple: true;
      value: string[];
      onChange: (codes: string[]) => void;
    });

const LanguageSelector: React.FC<LanguageSelectorProps> = (props) => {
  const { t } = useTranslation("common");
  const {
    value,
    languages = SUPPORTED_LANGUAGES,
    columns = 1,
    triggerMinWidth = 160,
    menuColumnWidth = 160,
    selectedDisplayMode = "name",
  } = props;
  const multiple = props.multiple === true;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState("");

  const normalizedSearch = search.trim().toLowerCase();
  const options = normalizedSearch
    ? languages.filter((lang) =>
        [lang.name, lang.nativeName, lang.code].some((field) =>
          field.toLowerCase().includes(normalizedSearch),
        ),
      )
    : languages;
  const selectedValues = Array.isArray(value) ? value : [value];
  const selected = languages.find((l) => l.code === selectedValues[0]);
  const columnCount = Math.max(1, Math.floor(columns));
  const getLocalizedLanguageName = (language: Language) =>
    t(`languageSelector.languageNames.${language.code}`, {
      defaultValue: language.name,
    });
  const getSelectedLabel = (language: Language) =>
    selectedDisplayMode === "code"
      ? language.code.toUpperCase()
      : getLocalizedLanguageName(language);
  const getTriggerLabel = () => {
    if (multiple) {
      const selectedLanguages = languages.filter((l) =>
        selectedValues.includes(l.code),
      );
      if (selectedLanguages.length === 0) {
        return t("languageSelector.selectLanguages");
      }
      if (selectedLanguages.length <= 2) {
        return selectedLanguages.map(getSelectedLabel).join(", ");
      }
      return t("languageSelector.languagesCount", {
        count: selectedLanguages.length,
      });
    }
    return selected
      ? getSelectedLabel(selected)
      : t("languageSelector.selectLanguage");
  };

  const handleOpen = (e: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const handleClose = () => {
    setAnchorEl(null);
    setSearch("");
  };
  const handleSelect = (code: string) => {
    if (props.multiple) {
      const nextValue = selectedValues.includes(code)
        ? selectedValues.filter((selectedCode) => selectedCode !== code)
        : [...selectedValues, code];
      props.onChange(nextValue);
      return;
    }
    props.onChange(code);
    handleClose();
  };

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          cursor: "pointer",
          px: "10px",
          py: "6px",
          // Border follows the trigger's text colour, like the arrow, so it
          // stays visible on the dark app bar as well as on page surfaces.
          border: "1px solid",
          borderColor: "color-mix(in srgb, currentColor 40%, transparent)",
          borderRadius: 1,
          minWidth: triggerMinWidth,
          userSelect: "none",
          "&:hover": { borderColor: "currentColor" },
        }}
      >
        <Typography variant="body2" sx={{ flex: 1 }}>
          {getTriggerLabel()}
        </Typography>
        <Typography
          variant="body2"
          sx={{ fontSize: 10, color: "inherit", opacity: 0.7 }}
        >
          ▾
        </Typography>
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: { mt: "4px", borderRadius: 2, boxShadow: 3 },
          },
        }}
      >
        <Box sx={{ p: "8px", pb: 0 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("languageSelector.searchPlaceholder", {
              defaultValue: "Search languages",
            })}
          />
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
            p: "8px",
            minWidth: menuColumnWidth * columnCount,
            maxHeight: 400,
            overflowY: "auto",
          }}
        >
          {options.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: "12px", py: "10px", gridColumn: "1 / -1" }}
            >
              {t("languageSelector.noResults", {
                defaultValue: "No languages found",
              })}
            </Typography>
          )}
          {options.map((lang) => {
            const isSelected = selectedValues.includes(lang.code);
            return (
              <Box
                key={lang.code}
                dir={lang.direction}
                onClick={() => handleSelect(lang.code)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: "12px",
                  py: "10px",
                  borderRadius: 1,
                  cursor: "pointer",
                  backgroundColor: isSelected
                    ? "rgba(25, 118, 210, 0.08)"
                    : "transparent",
                  color: isSelected ? colors.primary.main : "text.primary",
                  "&:hover": {
                    backgroundColor: isSelected
                      ? "rgba(25, 118, 210, 0.08)"
                      : (theme) => theme.palette.action.hover,
                  },
                }}
              >
                <Box>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    color={isSelected ? colors.primary.main : "text.primary"}
                  >
                    {getLocalizedLanguageName(lang)}
                  </Typography>
                  {lang.nativeName !== getLocalizedLanguageName(lang) && (
                    <Typography
                      variant="caption"
                      color={
                        isSelected ? colors.primary.main : "text.secondary"
                      }
                    >
                      ({lang.nativeName})
                    </Typography>
                  )}
                </Box>
                {isSelected && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: colors.primary.main,
                      ml: 1,
                      flexShrink: 0,
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Popover>
    </>
  );
};

export default LanguageSelector;
